// Copyright (c) 2025 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { catchError, filter, mergeMap } from "rxjs/operators"
import { create } from "@bufbuild/protobuf"
import { showSnackBar } from "../../snackbar/actions"
import * as Actions from "../actions"
import * as Slice from "../slice"
import { executor, Init, timesheetFieldService } from "../../../app/rpc"
import {
  BatchGetTimesheetFieldConfigsRequestSchema,
  GenerateTimesheetFieldConfigsRequestSchema,
  GenerateTimesheetFieldConfigsResponse,
} from "../../../generated/wisetime/site/timesheetfield/v1/timesheet_field_pb"

type GenerateTimesheetFieldConfigsRpc = (request: Init<typeof GenerateTimesheetFieldConfigsRequestSchema>) => Observable<GenerateTimesheetFieldConfigsResponse>

export const generateTimesheetFieldConfigs = (generateTimesheetFieldConfigsRpc: GenerateTimesheetFieldConfigsRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.generateTimesheetFieldConfigs.match),
      mergeMap(action => {
        const request = action.payload

        return generateTimesheetFieldConfigsRpc(request).pipe(
          mergeMap(response => {
            const actions: Action[] = []

            const { configRefs } = response
            if (configRefs.length) {
              actions.push(
                Actions.batchGetTimesheetFieldConfigs(
                  create(BatchGetTimesheetFieldConfigsRequestSchema, {
                    siteId: request.siteId,
                    configRefs,
                  })
                ),
              )
            } else {
              actions.push(
                Slice.setNoTimesheetFieldsSiteId(request.siteId ?? "")
              )
            }

            return concat(actions)
          }),
          catchError(() => of(showSnackBar({
            message: t({
              id: "msg_generate_timesheet_field_configs_failed",
              message: "Failed to load timesheet field settings. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
            }),
            messageType: "ERROR",
          }))),
        )
      }),
    )

const generateTimesheetFieldConfigsRpc = (request: Init<typeof GenerateTimesheetFieldConfigsRequestSchema>): Observable<GenerateTimesheetFieldConfigsResponse> =>
  executor(() => timesheetFieldService.generateTimesheetFieldConfigs(create(GenerateTimesheetFieldConfigsRequestSchema, request)))

export default generateTimesheetFieldConfigs(generateTimesheetFieldConfigsRpc)
